import React from 'react';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BudgetSection from './BudgetSection';
import { formatMoneyInput } from '../../../utils/moneyUtils';
import { formatCurrency, roundedFieldSx } from './budgetFormat';
import styles from './BudgetItems.module.css';

/** Atalho de adicionar item: ícone, rótulo e o "+" à direita. */
function AddTile({ icon, label, onClick }) {
  return (
    <button type="button" className={styles.addTile} onClick={onClick}>
      <span className={styles.addTileIcon}>{icon}</span>
      <span className={styles.addTileLabel}>{label}</span>
      <AddRoundedIcon className={styles.addTilePlus} />
    </button>
  );
}

function EmptyItems() {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyArt}>
        <span className={styles.emptyArtBack} />
        <span className={styles.emptyArtFront}>
          <ReceiptLongOutlinedIcon />
        </span>
      </span>
      <strong className={styles.emptyTitle}>Nenhum item ainda</strong>
      <span className={styles.emptyText}>Escolha um procedimento, um pacote ou escreva um item manual.</span>
    </div>
  );
}

/**
 * Itens do orçamento: atalhos para adicionar e a tabela editável
 * (descrição, quantidade, valor unitário, total da linha e remover).
 */
function BudgetItems({ items, onAddProcedure, onAddPackage, onAddManual, onUpdate, onRemove }) {
  return (
    <BudgetSection
      icon={<ReceiptLongOutlinedIcon />}
      title="Itens do orçamento"
      subtitle={items.length ? `${items.length} ${items.length === 1 ? 'item' : 'itens'}` : 'Adicione o que vai no orçamento'}
    >
      <div className={styles.addRow}>
        <AddTile icon={<AssignmentOutlinedIcon />} label="Adicionar procedimento" onClick={onAddProcedure} />
        <AddTile icon={<Inventory2OutlinedIcon />} label="Adicionar pacote" onClick={onAddPackage} />
        <AddTile icon={<EditNoteOutlinedIcon />} label="Item manual" onClick={onAddManual} />
      </div>

      {items.length === 0 ? (
        <EmptyItems />
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colIndex}>#</th>
                <th>Descrição</th>
                <th className={styles.colQty}>Qtd</th>
                <th className={styles.colPrice}>Valor unit.</th>
                <th className={styles.colTotal}>Total</th>
                <th className={styles.colActions} aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td className={styles.colIndex}>
                    <span className={styles.index}>{index + 1}</span>
                  </td>
                  <td>
                    <TextField
                      size="small"
                      fullWidth
                      sx={roundedFieldSx}
                      value={item.description}
                      onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
                      placeholder="Descrição do item"
                    />
                  </td>
                  <td className={styles.colQty}>
                    <TextField
                      size="small"
                      type="number"
                      sx={roundedFieldSx}
                      inputProps={{ min: 1, step: 1 }}
                      value={item.quantity}
                      onChange={(e) => onUpdate(item.id, 'quantity', e.target.value)}
                    />
                  </td>
                  <td className={styles.colPrice}>
                    <TextField
                      size="small"
                      sx={roundedFieldSx}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                      placeholder="0,00"
                      value={item.unitPriceText ?? formatMoneyInput(item.unitPrice)}
                      onChange={(e) => onUpdate(item.id, 'unitPrice', e.target.value)}
                      onBlur={() => onUpdate(item.id, 'unitPriceText', undefined)}
                    />
                  </td>
                  <td className={styles.colTotal}>{formatCurrency(item.totalPrice)}</td>
                  <td className={styles.colActions}>
                    <Tooltip title="Remover item" arrow>
                      <IconButton
                        size="small"
                        className={styles.removeButton}
                        onClick={() => onRemove(item.id)}
                        aria-label={`Remover ${item.description || 'item'}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BudgetSection>
  );
}

export default BudgetItems;
